import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ScoreRing } from '@/components/ScoreRing';
import { ChevronLeft, Footprints } from 'lucide-react';
import { format } from 'date-fns';

const GOAL = 10000;

export default function StepsLog() {
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [data, setData] = useState(null);
  const [input, setInput] = useState('');
  const [brisk, setBrisk] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/steps', { params: { date: today } }).then(r => {
    setData(r.data);
    setInput(v => (v === '' ? String(r.data.manual || '') : v));          // never overwrite what the client already typed
    setBrisk(v => (v === '' ? String(r.data.manual_brisk || '') : v));
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/steps', { date: today, steps: parseInt(input || '0', 10), brisk_steps: parseInt(brisk || '0', 10) });
      toast.success('Steps logged');
      nav(-1);
    }
    catch { toast.error('Failed'); setSaving(false); }
  };

  const steps = data?.steps || 0;
  const pct = Math.min(100, Math.round((steps / GOAL) * 100));

  return (
    <div className="space-y-5" data-testid="steps-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="back-btn" className="w-9 h-9 rounded-full bg-[var(--c-film)] flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display font-black text-2xl">Daily Movement</h1>
      </div>

      <div className="rounded-xl bg-[var(--c-film)] px-4 py-2.5 text-xs text-muted-foreground" data-testid="movement-info">
        Daily Movement is informational only — it tracks general steps and <span className="text-foreground/80">does not affect your Recovery Score</span>. Assigned Recovery Walks (e.g. Zone 2) count toward recovery.
      </div>

      <div className="regen-card-elevated p-6 flex flex-col items-center blueprint-grid">
        <ScoreRing value={pct} color='#65A30D' size={170} stroke={14}
          center={<div className="flex flex-col items-center"><Footprints className="w-5 h-5 text-[var(--c-green-ink)]" /><div className="font-mono text-3xl font-bold mt-1">{steps.toLocaleString()}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">of {GOAL.toLocaleString()}</div></div>} />
        <div className="mt-3 text-sm font-semibold text-[var(--c-green-ink)]">{pct}% of daily goal</div>
        {data?.activity > 0 && <div className="text-xs text-muted-foreground mt-1">{data.activity.toLocaleString()} from walking activity</div>}
      </div>

      <div className="regen-card-elevated p-5 space-y-3">
        <div className="font-display font-bold">Log Today's Movement</div>
        <p className="text-xs text-muted-foreground">Daily steps = all movement today, including your brisk walk. We use the highest value between your entry and any walking activity completed today.</p>
        <label className="text-xs text-muted-foreground">Daily steps (total)</label>
        <input type="number" inputMode="numeric" value={input} onChange={e => setInput(e.target.value)} data-testid="steps-input" placeholder="e.g. 10842"
          className="w-full bg-[var(--c-film)] border border-[var(--c-hair)] rounded-xl px-3 py-3 font-mono text-lg focus:outline-none focus:border-[var(--c-green)]" />
        <label className="text-xs text-muted-foreground">Brisk walk steps (part of the total)</label>
        <input type="number" inputMode="numeric" value={brisk} onChange={e => setBrisk(e.target.value)} data-testid="brisk-steps-input" placeholder="e.g. 7000"
          className="w-full bg-[var(--c-film)] border border-[var(--c-hair)] rounded-xl px-3 py-3 font-mono text-lg focus:outline-none focus:border-[var(--c-green)]" />
        <button onClick={save} disabled={saving} data-testid="save-steps-btn" className="w-full rounded-xl py-3 bg-[var(--c-green)] text-[#081726] font-bold tracking-wide active:scale-[0.99] transition disabled:opacity-60">
          {saving ? 'SAVING…' : 'SAVE STEPS'}
        </button>
      </div>
    </div>
  );
}
